import { map } from "bluebird";
import { type AppContext } from "~/lib/context";

import { isNil, uniqBy } from "~/lib/lodash";
import { logWarn } from "~/lib/logger";

type LevelPayloadParams = {
  companyId: number;
  externalId?: string | null;
  name: string | null;
};

export const buildLevelPayload = (params: LevelPayloadParams) => {
  const { companyId, externalId, name } = params;

  if (!name) {
    return null;
  }

  return {
    level: {
      connectOrCreate: {
        where: {
          companyId_externalId: {
            companyId,
            externalId: externalId ?? name,
          },
        },
        create: {
          externalId: externalId ?? name,
          name,
          company: {
            connect: { id: companyId },
          },
        },
      },
    },
  };
};

export type LevelPayload = ReturnType<typeof buildLevelPayload>;

export const getLevelUpdatePayload = (params: LevelPayload | undefined) => {
  if (isNil(params?.level)) {
    return null;
  }

  const {
    level: {
      connectOrCreate: { create },
    },
  } = params;

  return {
    levels: true,
    companyId: create.company.connect.id,
    externalId: create.externalId,
    data: create,
  };
};

export const updateLevelsPostSync = async (ctx: AppContext, levels: NonNullable<LevelUpdatePayload>[]) => {
  const uniqueLevels = uniqBy(levels, (level) => level.companyId + level.externalId);

  return map(uniqueLevels, async (payload) => {
    const { companyId, externalId, data } = payload;

    const existingLevel = await ctx.prisma.externalLevel.findUnique({
      where: { companyId_externalId: { companyId, externalId } },
    });

    if (!existingLevel) {
      logWarn(ctx, "[updateLevelsPostSync] Level not found", {
        companyId,
        externalId,
      });
    }

    if (existingLevel) {
      return ctx.prisma.externalLevel.update({
        where: { id: existingLevel.id },
        data,
      });
    }
  });
};

export type LevelUpdatePayload = ReturnType<typeof getLevelUpdatePayload>;
